import { ActionDto } from '@issuemanager/api-client';
import { useEventCallback } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { AssignIssueFormValues } from './use-manage-issue-form';
import { useIssueOutletContext } from './useIssueOutletContext';

type Params = {
  manageIssueForm: UseFormReturn<
    AssignIssueFormValues,
    any,
    AssignIssueFormValues
  >;
};

const mapActions = (actionsData: ActionDto[]) => {
  const owners: ActionDto['owner'][] = [];
  const approvers: ActionDto['approver'][] = [];

  const mappedActions = actionsData.map((action) => {
    const owner = action.owner ?? null;
    const approver = action.approver ?? null;

    if (owner) owners.push(owner);
    if (approver) approvers.push(approver);

    return {
      ...action,
      ownerId: owner?.id,
      approverId: approver?.id,
      // NOTE: govmapGroups aren't applied yet. Ticket RCP-12663
      govmapGroups: [],
    };
  });

  return mappedActions;
};

export const useResetFormData = ({
  manageIssueForm,
}: Params): { resetForm: () => void } => {
  const { issue } = useIssueOutletContext();

  const { data: issueData } = issue;

  const resetFormFromIssueData = useEventCallback(() => {
    if (!issueData) return;

    const actions = mapActions(issueData.actions);

    manageIssueForm.reset({
      title: issueData.title,
      description: issueData.description ?? '',
      issueType: issueData.issueType?.name,
      executionType: issueData.executionType,
      severity: issueData.severity,
      ownerId: issueData.owner?.id,
      approverId: issueData.approver?.id,
      watcherIds: issueData.watchers?.map((w) => w.id) ?? [],
      dueDate: issueData.dueDate ?? undefined,
      govmapGroups: issueData.govmapGroups?.map((group) => group.id) ?? [],
      attachments: issueData.attachments,
      impact: issueData.impact ?? '',
      classification: issueData.classification,
      resolution: issueData.resolution,
      rootCause: issueData.rootCause ?? '',
      recommendations: issueData.recommendations ?? '',
      managementResponse: issueData.managementResponse ?? '',
      includeInAuditReport: issueData.includeInAuditReport ?? true,
      remediation: issueData.remediation ?? undefined,
      datePublished: issueData.datePublished ?? undefined,
      actions: actions.map(
        ({ owner: _owner, approver: _approver, ...action }) => ({
          ...action,
          customId: action.id,
        }),
      ),
    });
  });

  const resetForm = useEventCallback(() => {
    if (!issueData) return;
    resetFormFromIssueData();
  });

  return {
    resetForm,
  };
};

export default useResetFormData;
